/**
 * « Proposer une mesure » (PRD §14.5) : champ texte + dictée, et carte d'estimation en direct du service de chiffrage
 * (bloc F, ADR 0007) — fourchette, curseur d'hypothèse, bandeau « Estimation IA — non sourcée », bouton « Ajouter à mon
 * programme ». État désactivé (libellé `UNAVAILABLE_MSG`) tant que l'API n'est pas ouverte ; message du serveur si elle
 * est ouverte mais en échec, refus motivé ou plafond atteint.
 */
import type { MeasureCategory } from "../../../engine/src/types.js";
import { CATEGORY_LABELS } from "../content-types.js";
import { costAsBalance, escapeHtml as e, num } from "../format.js";
import { listProposals, SOURCED_EVENT, speechAvailable, type EstimatedMeasure, type SourcedUpdate } from "../proposals.js";
import { icon } from "../icons.js";

/** Dernière estimation reçue par catégorie (+ valeur du curseur choisie avant l'ajout), conservée d'un rendu à l'autre. */
export const lastEstimates = new Map<MeasureCategory, { est: EstimatedMeasure; hyp?: number }>();

export const UNAVAILABLE_MSG = "Simulation en direct : disponible dès l'ouverture du service de chiffrage (bloc F)";

const safeUrl = (u: string) => (/^https?:\/\/[^\s"'<>]+$/i.test(u) ? u : "");

/** Badge selon le niveau : aperçu provisoire non sourcé, chiffrage sourcé (2 sources) ou à une source (ADR 0013). */
export function tierBadge(est: EstimatedMeasure | null): string {
  if (!est || est.status === "estimé_ia") return `<span class="badge ai">Estimation provisoire, non sourcée</span>`;
  const n = est.sourced?.independent_publishers;
  return est.status === "estimé_ia_source"
    ? `<span class="badge ok">Chiffrage sourcé${n ? ` — ${n} sources indépendantes` : ""}, non vérifié par le fact-checker</span>`
    : `<span class="badge">Chiffrage IA — une seule source</span>`;
}

/** Bloc « sources et méthode » d'un chiffrage sourcé (citations courtes, valeur lue, lien vers la source). */
export function sourcedDetails(est: EstimatedMeasure): string {
  const s = est.sourced;
  if (!s || s.sources.length === 0) return "";
  const items = s.sources
    .map((x) => {
      const url = safeUrl(x.url);
      const title = url ? `<a href="${e(url)}" target="_blank" rel="noopener noreferrer">${e(x.title)}</a>` : e(x.title);
      return `<li><b>${e(x.publisher)}</b>, ${title}${x.year ? ` (${x.year})` : ""} — « ${e(x.quote)} » <span class="sm mut">valeur lue : ${e(x.value_read)} · consulté le ${e(x.accessed)}${x.quote_verified ? "" : " · citation à vérifier"}</span></li>`;
    })
    .join("");
  const method = s.method.map((m) => `<li>${e(m.label)} : ${num(m.value, 2)} ${e(m.unit)}${m.source_index !== null ? ` <span class="sm mut">[source ${m.source_index + 1}]</span>` : m.formula ? ` <span class="sm mut">(${e(m.formula)})</span>` : ""}</li>`).join("");
  return `<details class="more" open><summary>Sources (${s.sources.length}) et méthode</summary><ol class="srcs">${items}</ol>${method ? `<p class="l">Calcul</p><ul class="method">${method}</ul>` : ""}${s.uncertainty ? `<p class="hint">Incertitude : ${e(s.uncertainty)}</p>` : ""}${s.explanation ? `<p class="hint">${e(s.explanation)}</p>` : ""}</details>`;
}

/** Ligne d'état sous la carte : déjà chiffrée / variante / en cours / reporté, et « vu X fois ». */
export function tierStatus(est: EstimatedMeasure): string {
  const seen = est.hit_count > 1 ? ` Vue ${est.hit_count} fois.` : "";
  if (est.tier === "sourced") {
    const why =
      est.path === "formula"
        ? "Variante calculée par la formule d'une proposition déjà chiffrée : réponse instantanée, sans nouvel appel."
        : est.path === "new"
          ? "Chiffrage sourcé terminé et enregistré : les prochains joueurs l'obtiendront instantanément."
          : "Proposition déjà chiffrée : réponse instantanée, sans nouvel appel.";
    return `<p class="hint">${why}${seen}${est.replaces ? " Ajoute cette version : elle remplace l'estimation provisoire dans ton programme." : ""}</p>`;
  }
  const pending = est.job && (est.job.status === "queued" || est.job.status === "running");
  return `<p class="hint ${pending ? "" : "warn"}" ${pending ? `aria-live="polite"` : ""}>${e(est.notice ?? "Chiffrage automatique, non relu, sans source : à prendre comme un ordre de grandeur.")}${est.cached && !pending ? " (Réponse déjà calculée pour une proposition identique.)" : ""}${seen}</p>`;
}

/** Carte d'estimation : `est` absent = état désactivé (placeholders « — »). `added` = déjà dans le programme. */
export function estimateCard(est: EstimatedMeasure | null, message: string = UNAVAILABLE_MSG, opts: { added?: boolean; hyp?: number | undefined } = {}): string {
  const disabled = !est;
  const range = est ? `${costAsBalance(est.cost_bn.low)} à ${costAsBalance(est.cost_bn.high)} Md€/an` : "— à — Md€/an";
  const central = est ? `${costAsBalance(est.cost_bn.central)}` : "—";
  const h = est?.hyp;
  const hv = h ? (opts.hyp ?? h.default) : undefined;
  const cat = est?.category ?? "";
  return `<div class="est ${disabled ? "off" : ""}" aria-disabled="${disabled}">
    <div class="estban">${tierBadge(est)}</div>
    <div class="estrow"><div><div class="l">Coût ou rendement estimé</div><div class="v">${central}<small> Md€/an</small></div><div class="l">fourchette ${range}</div></div>
    <div class="l">${est ? e(est.label) : "Libellé reformulé, bénéficiaires, effet prix"}</div></div>
    <label class="hyp">${h ? e(h.label) : "Hypothèse dominante"} : <b ${est ? `id="esthv-${cat}"` : ""}>${hv !== undefined ? num(hv, 1) : "—"}</b>
      <input type="range" min="${h?.low ?? 0}" max="${h?.high ?? 1}" step="any" value="${hv ?? 0}" ${disabled ? "disabled" : `data-esthyp="${cat}"`} aria-label="Hypothèse de l'estimation"></label>
    ${est && h ? `<p class="hint">Bornes du curseur : proposées par l'estimation IA${est.tier === "sourced" ? " à partir des sources ci-dessous" : " (non sourcées)"}. Le moteur applique coût × (1 − hypothèse/100).</p>` : ""}
    <button type="button" class="sec" ${est && !opts.added ? `data-propadd="${cat}"` : "disabled"}>${opts.added ? "Dans mon programme" : "Ajouter à mon programme"}</button>
    <p class="hint ${disabled ? "warn" : ""}">${e(est ? est.rationale : message)}</p>
    ${est ? sourcedDetails(est) + tierStatus(est) : ""}
  </div>`;
}

/**
 * Remplacement de l'aperçu par la version sourcée quand le job se termine (événement émis par ui/src/proposals.ts) :
 * seulement si la carte montre toujours l'aperçu suivi (le joueur n'a pas lancé une autre proposition entre-temps).
 */
export function applySourcedUpdate(u: SourcedUpdate, doc: Pick<Document, "getElementById"> | null = typeof document !== "undefined" ? document : null): boolean {
  const cur = lastEstimates.get(u.category);
  if (!cur || cur.est.key !== u.previewKey) return false;
  if (u.est) lastEstimates.set(u.category, { est: u.est });
  else lastEstimates.set(u.category, { ...cur, est: { ...cur.est, notice: u.message ?? cur.est.notice ?? "", ...(cur.est.job ? { job: { ...cur.est.job, status: "failed" as const } } : {}) } });
  const out = doc?.getElementById(`propout-${u.category}`);
  if (out) out.innerHTML = estimateCard(lastEstimates.get(u.category)!.est, UNAVAILABLE_MSG);
  return true;
}

if (typeof document !== "undefined" && typeof document.addEventListener === "function") {
  document.addEventListener(SOURCED_EVENT, (ev) => void applySourcedUpdate((ev as CustomEvent<SourcedUpdate>).detail));
}

export function proposalBox(cat: MeasureCategory, opts: { lastStatus?: string; isAdded?: (measureId: string) => boolean } = {}): string {
  const last = lastEstimates.get(cat);
  const mine = listProposals(cat);
  const mic = speechAvailable()
    ? `<button type="button" class="sec mic" data-mic="${cat}" aria-label="Dicter la proposition">Dicter</button>`
    : `<button type="button" class="sec mic" disabled title="Dictée non disponible dans ce navigateur">Dicter</button>`;
  return `<div class="prop" data-prop="${cat}">
    <label class="t" for="prop-${cat}">Proposer une mesure · ${e(CATEGORY_LABELS[cat])}</label>
    <p class="hint">Écris ou dicte ton idée (« taxe sur les produits sucrés », « aide aux parents isolés »…).</p>
    <textarea id="prop-${cat}" rows="2" maxlength="400" placeholder="Ma proposition…" data-proptext="${cat}"></textarea>
    <div class="row">${mic}<button type="button" data-propsubmit="${cat}">Estimer</button></div>
    <div class="propout" id="propout-${cat}">${last ? estimateCard(last.est, UNAVAILABLE_MSG, { added: opts.isAdded?.(last.est.measure.id) ?? false, hyp: last.hyp }) : opts.lastStatus ? estimateCard(null) : ""}</div>
    ${
      mine.length
        ? `<details class="more"><summary>Mes propositions (${mine.length}), enregistrées sur cet appareil</summary><ul class="props">${mine
            .map((p) => `<li><span>${e(p.text)}</span> <button type="button" class="ghost" data-propdel="${p.id}" aria-label="Supprimer">${icon("fermer", 20)}</button></li>`)
            .join("")}</ul></details>`
        : ""
    }
  </div>`;
}
